import { AutoDelete, DeleteForever, Refresh } from "@mui/icons-material";
import {
  Box,
  Card,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridValueFormatterParams,
} from "@mui/x-data-grid";
import { AccountPicker } from "components/holdings/AccountPicker";
import { AccountStrategyPicker } from "components/holdings/AccountStrategyPicker";
import { AddAccountStrategyModal } from "components/holdings/AddAccountStrategyModal";
import { AdjustmentHistoryGrid } from "components/clients/strategy/AdjustmentHistoryGrid";
import { YesNoSelect } from "components/ui/YesNoSelect";
import dayjs from "dayjs";
import { ConfirmDeleteStrategyModal } from "components/clients/ConfirmDeleteStrategyModal";
import { useAppDispatch, useTrading } from "features/store";
import {
  AccountStrategy,
  useListAccountStrategiesQuery,
  useRemoveAccountFromStrategyMutation,
  useCompletelyDeleteAccountStrategyMutation,
} from "features/trading/accounts";
import {
  SelectedStrategyKind,
  setSelectedStrategy,
} from "features/trading/tradingSlice";
import { useState } from "react";
import { useSnackbar } from "notistack";
import {
  NOTISTACK_FAILURE_OPTIONS,
  NOTISTACK_SUCCESS_OPTIONS,
} from "constants/index";
import AddIcon from "@mui/icons-material/Add";

const columns: GridColDef[] = [
  {
    field: "composite",
    headerName: "Composite",
    type: "boolean",
    renderCell: (params: any) => <YesNoSelect value={params.value} />,
  },
  {
    field: "reporting",
    headerName: "Reporting",
    type: "string",
  },
  {
    field: "reinvest",
    headerName: "Reinvest $",
    type: "boolean",
    renderCell: (params: any) => <YesNoSelect value={params.value} />,
  },
  {
    field: "keywork",
    headerName: "Keyword",
    type: "boolean",
    renderCell: (params: any) => <YesNoSelect value={params.value} />,
  },
  {
    field: "underlying",
    headerName: "Underlying",
    type: "string",
    valueFormatter: ({ value }: GridValueFormatterParams) => value || "N/A",
  },
  {
    field: "management",
    headerName: "Management",
    type: "string",
    valueFormatter: ({ value }: GridValueFormatterParams) => value || "N/A",
  },
  {
    field: "advisor__name",
    headerName: "Advisor",
    type: "string",
    valueFormatter: ({ value }: GridValueFormatterParams) => value || "N/A",
  },
].map((col) => ({
  ...col,
  align: "center",
}));

const DUMMY_COMPOSITE_HISTORY = [
  {
    id: 1,
    as_of_date: "2024-01-14",
    composite: true,
    reporting: "Quarterly",
    reinvest: true,
    keywork: true,
    underlying: "Covered",
    management: "Direct",
    advisor__name: "David Watts",
    notes: "Notes go here, them might be long I'm not sure",
  },
  {
    id: 2,
    as_of_date: "2023-12-15",
    composite: false,
    reporting: "Annual",
    reinvest: true,
    keywork: true,
    underlying: "Uncovered",
    management: "Yes",
    advisor__name: "David Watts",
    notes: "Did not look like a good idea",
  },
  {
    id: 3,
    as_of_date: "2023-11-13",
    composite: true,
    reporting: "Monthly",
    reinvest: true,
    keywork: true,
    underlying: "Covered",
    management: "Direct",
    advisor__name: "David Watts",
    notes: "Nothing to say here",
  },
];

interface StrategyProps {
  initialStrategyId?: number | null;
}

export default function Strategy({ initialStrategyId }: StrategyProps) {
  const dispatch = useAppDispatch();
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const { selectedStrategy, currentAccountId } = useTrading();
  const strategyId =
    selectedStrategy?.kind === SelectedStrategyKind.REMOVED
      ? selectedStrategy.data.id
      : selectedStrategy?.data.strategy;
  const { data: accountStrategiesData } = useListAccountStrategiesQuery(
    { filters: { account_id: currentAccountId } },
    { skip: !currentAccountId },
  );
  const activeAccountStrategy =
    selectedStrategy?.kind === SelectedStrategyKind.ACTIVE
      ? accountStrategiesData?.results.find(
          (s) => s.id === selectedStrategy.data.id,
        )
      : undefined;
  const [removeAccountFromStrategy] = useRemoveAccountFromStrategyMutation();
  const [completelyDeleteAccountStrategy] =
    useCompletelyDeleteAccountStrategyMutation();
  const { enqueueSnackbar } = useSnackbar();
  const rows: Partial<AccountStrategy>[] = [
    ...(activeAccountStrategy ? [activeAccountStrategy] : []),
    ...(showHistory ? DUMMY_COMPOSITE_HISTORY : []),
  ];

  const [isOpenedAddModal, setIsOpenAddModal] = useState<boolean>(false);
  const openAddModal = () => setIsOpenAddModal(true);
  const onAddModalClose = () => setIsOpenAddModal(false);

  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] =
    useState<boolean>(false);

  const onRemoveAccountStrategy = () => {
    if (currentAccountId !== undefined && activeAccountStrategy) {
      removeAccountFromStrategy({
        id: currentAccountId,
        strategy_id: activeAccountStrategy.strategy,
      });
    }
    dispatch(setSelectedStrategy(undefined));
    setShowHistory(false);
  };

  const handleCompletelyDeleteStrategy = async () => {
    if (currentAccountId === undefined || !activeAccountStrategy) return;
    try {
      await completelyDeleteAccountStrategy({
        id: currentAccountId,
        strategy_id: activeAccountStrategy.strategy,
      }).unwrap();
      enqueueSnackbar("Strategy completely deleted", NOTISTACK_SUCCESS_OPTIONS);
      dispatch(setSelectedStrategy(undefined));
      setShowHistory(false);
    } catch {
      enqueueSnackbar("Failed to delete strategy", NOTISTACK_FAILURE_OPTIONS);
    } finally {
      setIsConfirmDeleteOpen(false);
    }
  };

  return (
    <Box>
      <Card>
        <Box display={"flex"}>
          <AccountPicker />
          <Box marginLeft={1}>
            <AccountStrategyPicker initialStrategyId={initialStrategyId} />
          </Box>
          <Box flexGrow={1} marginLeft={1}>
            <Tooltip title="Remove account from selected strategy">
              <IconButton
                onClick={onRemoveAccountStrategy}
                disabled={!activeAccountStrategy}
              >
                <AutoDelete />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add new strategy to account">
              <IconButton onClick={openAddModal}>
                <AddIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Completely delete strategy (removes all history)">
              <span>
                <IconButton
                  onClick={() => setIsConfirmDeleteOpen(true)}
                  disabled={!activeAccountStrategy}
                >
                  <DeleteForever />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <IconButton
            onClick={() => setShowHistory((oldValue) => !oldValue)}
            disabled={!selectedStrategy}
          >
            <Typography>{showHistory ? "Hide" : "Show"} History</Typography>
            <Refresh />
          </IconButton>
        </Box>
      </Card>
      {selectedStrategy ? (
        rows.map((row, index) => (
          <Card key={row.id}>
            <Box margin={1}>
              <Typography variant="body1" color={"green"}>
                As of {dayjs().format("MM/DD/YYYY")}
              </Typography>
              <DataGrid hideFooter columns={columns} rows={[rows[index]]} />
              <Typography
                variant="body2"
                color={"primary.main"}
                fontWeight={"bold"}
                marginY={2}
              >
                Composite Inclusion/Exclusion Notes
              </Typography>
              <Divider />
              {index === 0 ? (
                <TextField multiline fullWidth value={row.notes} />
              ) : (
                <Typography variant="body2" marginY={2}>
                  {row.notes}
                </Typography>
              )}
            </Box>
          </Card>
        ))
      ) : (
        <Card>
          <Stack
            height="100%"
            alignItems="center"
            justifyContent="center"
            sx={{ padding: 2, marginY: 2 }}
          >
            Select account strategy
          </Stack>
        </Card>
      )}
      <Card sx={{ padding: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography fontWeight={"bold"} color={"primary.main"}>
              Accumulated Cashflow:
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography>$2,493</Typography>
          </Grid>
          <Grid item xs={7}>
            <Typography color={"green"}>*Since Inception</Typography>{" "}
          </Grid>
          <Grid item xs={3}>
            <Typography fontWeight={"bold"} color={"primary.main"}>
              Current Unrealized G/L:
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography>$12,493</Typography>
          </Grid>
          <Grid item xs={7}></Grid>
          <Grid item xs={3}>
            <Typography fontWeight={"bold"} color={"primary.main"}>
              1 Yr, 2 Yr, Drop Cashflow:
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography>$29,492</Typography>
          </Grid>
          <Grid item xs={7}></Grid>
        </Grid>
      </Card>
      <AdjustmentHistoryGrid
        accountId={currentAccountId}
        strategyId={strategyId}
        canCreate={!!selectedStrategy && !!currentAccountId}
        accountStrategy={activeAccountStrategy}
      />
      <AddAccountStrategyModal
        isOpened={isOpenedAddModal}
        onClose={onAddModalClose}
        accountId={currentAccountId!}
      />
      <ConfirmDeleteStrategyModal
        isOpened={isConfirmDeleteOpen}
        onClose={() => setIsConfirmDeleteOpen(false)}
        onConfirm={handleCompletelyDeleteStrategy}
      />
    </Box>
  );
}
